import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import { MastersProvider } from './common/MastersContext';
import Applications from './tabs/Applications';
import PhoneInterview from './tabs/PhoneInterview';
import Demo from './tabs/Demo';
import PersonalInterview from './tabs/PersonalInterview';
import FinalInterview from './tabs/FinalInterview';
import Recruitment from './tabs/Recruitment';
import Hold from './tabs/Hold';
import Rejection from './tabs/Rejection';
import Report from './tabs/Report';
import Search from './tabs/Search';

/**
 * The recruitment pipeline, one tab per stage — the same order the old
 * aapshrm job_candidates screen used.
 */
const TABS = [
  { key: 'applications', label: 'Applications', Component: Applications },
  { key: 'phone', label: 'Phone Interview', Component: PhoneInterview },
  { key: 'demo', label: 'Demo', Component: Demo },
  { key: 'personal', label: 'Personal Interview 1', Component: PersonalInterview },
  { key: 'final', label: 'Final Interview', Component: FinalInterview },
  { key: 'recruitment', label: 'Recruitment', Component: Recruitment },
  { key: 'hold', label: 'Hold', Component: Hold },
  { key: 'rejection', label: 'Rejection', Component: Rejection },
  { key: 'report', label: 'Report', Component: Report },
  { key: 'search', label: 'Search', Component: Search },
];

/** Where a candidate lands when a dialog moves them on. */
const TAB_FOR_STATUS = {
  Applied: 'applications',
  Shortlisted: 'phone',
  Demo: 'demo',
  Interviewed: 'personal',
  Final_interview: 'final',
  Recruited: 'recruitment',
  Recruitment: 'recruitment',
  Hold: 'hold',
  hold: 'hold',
  Rejected: 'rejection',
};

/**
 * "Recruited" does not name the same destination everywhere: from Personal
 * Interview and Hold it moves the candidate into Recruitment, but from Final
 * Interview it is that tab's stay-here option. So the tab that submitted the
 * change gets the final say on where the candidate landed.
 */
const TAB_FOR_STATUS_FROM = {
  final: { Recruited: 'final' },
};

const JobManagement = () => {
  const history = useHistory();
  const [active, setActive] = useState('applications');

  // Remounting on tab change is what makes each tab refetch on the way back in.
  const [visitToken, setVisitToken] = useState(0);

  const openTab = (key) => {
    setActive(key);
    setVisitToken((n) => n + 1);
  };

  /** A dialog moved someone on — follow them to the tab they landed in. */
  const handleMoved = (status) => {
    const target = TAB_FOR_STATUS_FROM[active]?.[status] ?? TAB_FOR_STATUS[status];
    if (target && target !== active) openTab(target);
  };

  /**
   * A candidate became an employee — open their record in HR Management. The
   * detail screen reads the new user_id off the query string, the same link the
   * employee list uses. The short pause is just long enough to read the toast,
   * which goes with this screen when the route changes.
   */
  const handleJoined = (userId) => {
    toast.success('Employee record created successfully.');

    setTimeout(() => history.push(`/employees-detail?id=${userId}`), 1000);
  };

  const Active = TABS.find((tab) => tab.key === active)?.Component ?? Applications;

  return (
    <MastersProvider>
      <div className="section-body mt-3">
        <div className="container-fluid">
          <ToastContainer position="bottom-right" autoClose={3000} />

          <div className="d-flex flex-wrap mb-3" style={{ gap: 6 }}>
            {TABS.map((tab) => (
              <button
                key={tab.key}
                type="button"
                className={`btn btn-sm ${active === tab.key ? 'btn-primary' : 'btn-outline-secondary'}`}
                onClick={() => openTab(tab.key)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <Active key={`${active}-${visitToken}`} onMoved={handleMoved} onJoined={handleJoined} />
        </div>
      </div>
    </MastersProvider>
  );
};

export default JobManagement;
